import { type AppNode } from '../types';
import { stripSmartResizeFormatIdFromOverlayLayers } from './group/smartResizeBaseImage';

const sortAgentGroupIds = (groupIds: string[]): string[] =>
  [...groupIds].sort((a, b) => {
    // Group ids may be namespaced per run (e.g. "resize-0__<uuid>").
    const matchA = /^resize-(\d+)/.exec(a);
    const matchB = /^resize-(\d+)/.exec(b);

    if (matchA && matchB) {
      return Number(matchA[1]) - Number(matchB[1]);
    }

    return a.localeCompare(b);
  });

/** Media nodes a smart resize can produce — video reframes tag the same way images do. */
const isTaggableMediaNode = (node: AppNode): boolean =>
  (node.type === 'image' && node.data.type === 'image') || (node.type === 'video' && node.data.type === 'video');

const hasSmartResizeFormatId = (node: AppNode): boolean =>
  isTaggableMediaNode(node) && (node.data as { smartResizeFormatId?: string }).smartResizeFormatId !== undefined;

const getSmartResizeFormatId = (node: AppNode): string | undefined => {
  if (node.type === 'image' && node.data.type === 'image') {
    return node.data.smartResizeFormatId;
  }

  if (node.type === 'video' && node.data.type === 'video') {
    return node.data.smartResizeFormatId;
  }

  return undefined;
};

/**
 * Fallback: map toolbar-selected format ids onto output nodes when the agent did not
 * set smartResizeFormatId on edits. Flat outputs by index; grouped outputs by agent
 * group id (one format per group, applied to the base image layer).
 */
export const applySmartResizeFormatIds = (nodes: AppNode[], formatIds: string[]): void => {
  if (formatIds.length === 0) {
    return;
  }

  const groupIds = sortAgentGroupIds([
    ...new Set(nodes.map((node) => node.groupId).filter((id): id is string => Boolean(id))),
  ]);
  const groupIdToFormatIndex = new Map(groupIds.map((id, index) => [id, index]));
  const baseImageIdByGroup = new Map<string, string>();

  for (const node of nodes) {
    if (!isTaggableMediaNode(node) || !node.groupId) {
      continue;
    }

    const existingId = baseImageIdByGroup.get(node.groupId);
    const existingNode = existingId ? nodes.find((candidate) => candidate.id === existingId) : undefined;

    if (!existingNode || (node.zIndex ?? 0) < (existingNode.zIndex ?? 0)) {
      baseImageIdByGroup.set(node.groupId, node.id);
    }
  }

  // An id the agent already assigned is spoken for — the positional fallback must not hand it out again.
  const claimedFormatIds = new Set(nodes.map(getSmartResizeFormatId).filter((id): id is string => id !== undefined));
  const availableFormatIds = formatIds.filter((id) => !claimedFormatIds.has(id));

  let flatIndex = 0;

  for (const node of nodes) {
    if (!isTaggableMediaNode(node) || hasSmartResizeFormatId(node)) {
      continue;
    }

    const groupId = node.groupId;
    let formatId: string | undefined;

    if (groupId) {
      if (node.id !== baseImageIdByGroup.get(groupId)) {
        continue;
      }

      formatId = formatIds[groupIdToFormatIndex.get(groupId) ?? 0];
    } else {
      formatId = availableFormatIds[flatIndex];
      flatIndex += 1;
    }

    // Branch per media type rather than assigning through the union — `node.data` is a
    // union across every node kind, so a single spread widens past what it accepts.
    if (formatId && node.type === 'image' && node.data.type === 'image') {
      node.data = { ...node.data, smartResizeFormatId: formatId };
    } else if (formatId && node.type === 'video' && node.data.type === 'video') {
      node.data = { ...node.data, smartResizeFormatId: formatId };
    }
  }

  // Agent responses may tag every split layer; only the base image should be previewable.
  stripSmartResizeFormatIdFromOverlayLayers(nodes);
};
